// @ts-nocheck
/**
 * Saga factories for all Order pages (orderByStore, orderBySku, orderByKit).
 *
 * Each factory returns a generator function that can be registered with
 * `takeLatest` in the variant's root saga.  The variant only needs to
 * supply a small config object describing the differences.
 */
import { put, call } from 'redux-saga/effects';
import { getAPIDataV3, postAPIDataV3 } from 'utils/api';
import { REQUESTS } from 'utils/services/thetaretail/requests';
import { updateGridData, updateMetadata } from 'models/dynamicGrid/reducer';

/* ------------------------------------------------------------------ */
/*  createFetchColumnsSaga                                            */
/* ------------------------------------------------------------------ */

/**
 * Factory: creates a generator for fetching & classifying table columns.
 *
 * @param primaryGroupKey  — bucket name for the primary entity ('sku' | 'kit')
 * @param primaryKeywords  — keyword list that identifies primary-entity columns
 * @param columnKeys       — all bucket keys, e.g. ['sku','store','warehouse','order']
 * @param logPrefix        — console log prefix, e.g. '[OBS-Store]'
 * @param slice            — the variant's Redux slice (has setLoading, setColumns)
 * @param constants        — the variant's constants module (re-exports shared KW lists)
 */
export function createFetchColumnsSaga({
	primaryGroupKey,
	primaryKeywords,
	columnKeys,
	logPrefix,
	slice,
	constants,
}) {
	return function* handleFetchColumns({ payload: { tableId } }) {
		try {
			yield put(slice.setLoading({ columns: true }));

			const response = yield call(getAPIDataV3, {
				service: REQUESTS.GET.GET_TABLE_COLUMNS_MONGO.service,
				url: REQUESTS.GET.GET_TABLE_COLUMNS_MONGO.url,
				pathParams: { tableId },
			});

			const raw = response?.data?.data || response?.data || [];
			const cols = (Array.isArray(raw) ? raw : [])
				.filter((c) => !c.IsInternalColumn)
				.filter((c) => !constants.EXCLUDED_FILTER_COLUMNS.includes(c.AppColumnName))
				.map((c) => ({
					columnName: c.AppColumnName,
					displayName: c.AppColumnAliasName || c.AppColumn || c.AppColumnName,
					type: c.ColumnType,
				}));

			// Initialize column buckets
			const buckets = {};
			for (const key of columnKeys) buckets[key] = [];

			cols.forEach((col) => {
				const l = (col.displayName + ' ' + col.columnName).toLowerCase();
				if (constants.COLUMN_EXCLUDE_KW.some((kw) => l.includes(kw))) return;
				if (constants.COLUMN_ORDER_KW.some((kw) => l.includes(kw))) buckets.order.push(col);
				else if (constants.COLUMN_WAREHOUSE_KW.some((kw) => l.includes(kw))) buckets.warehouse.push(col);
				else if (constants.COLUMN_STORE_KW.some((kw) => l.includes(kw))) buckets.store.push(col);
				else if (primaryKeywords.some((kw) => l.includes(kw))) buckets[primaryGroupKey].push(col);
			});

			yield put(slice.setColumns(buckets));
		} catch (e) {
			console.error(`${logPrefix} Column fetch failed:`, e);
		} finally {
			yield put(slice.setLoading({ columns: false }));
		}
	};
}

/* ------------------------------------------------------------------ */
/*  createFetchOrderDataSaga                                          */
/* ------------------------------------------------------------------ */

/**
 * Factory: creates a generator for fetching order data with progressive
 * background loading.
 *
 * @param selectRowKey — row key for dynamicGrid metadata (e.g. constants.SELECT_ROW_KEY)
 * @param logPrefix    — console log prefix
 * @param slice        — the variant's Redux slice
 * @param constants    — the variant's constants module
 */
export function createFetchOrderDataSaga({ selectRowKey, logPrefix, slice, constants }) {
	return function* handleFetchOrderData({ payload: { tableId, orderStatusValue } }) {
		try {
			yield put(slice.setLoading({ data: true, backgroundData: false }));

			const statusFilter = {
				field: 'OrderStatusID',
				value: orderStatusValue,
				type: 'number',
				queryName: 'OrderStatusID',
			};
			const ts = new Date().getTime().toString();

			const firstRes = yield call(postAPIDataV3, {
				service: REQUESTS.POST.GET_GRID_DETAILS_V2.service,
				url: REQUESTS.POST.GET_GRID_DETAILS_V2.url,
				pathParams: { tableId },
				body: {
					pageNumber: 1,
					itemsPerPage: constants.PAGE_SIZE,
					search: JSON.stringify({ filter: [statusFilter] }),
					isAdvancedSearch: 1,
					refresh: ts,
				},
			});

			const firstRows = firstRes?.data?.data || firstRes?.data || [];
			const allRows = Array.isArray(firstRows) ? [...firstRows] : [];
			const totalCount = allRows[0]?.TotalRecordCount || allRows.length;

			yield put(slice.setOrderData({ rows: allRows, totalCount }));
			// Send separate copies to dynamicGrid so Immer freezing doesn't affect our rows
			yield put(updateGridData({ tableId, data: allRows.map((r) => ({ ...r })) }));
			yield put(updateMetadata({ tableId, metaData: { selectRowKey } }));
			yield put(slice.setLoading({ data: false }));

			/* -- Fetch remaining pages in background -- */
			if (totalCount > constants.PAGE_SIZE) {
				yield put(slice.setLoading({ backgroundData: true }));
				const pageCount = Math.ceil(totalCount / constants.PAGE_SIZE);
				const promises = [];
				for (let p = 2; p <= pageCount && p <= constants.MAX_BACKGROUND_PAGES; p++) {
					promises.push(
						postAPIDataV3({
							service: REQUESTS.POST.GET_GRID_DETAILS_V2.service,
							url: REQUESTS.POST.GET_GRID_DETAILS_V2.url,
							pathParams: { tableId },
							body: {
								pageNumber: p,
								itemsPerPage: constants.PAGE_SIZE,
								search: JSON.stringify({ filter: [statusFilter] }),
								isAdvancedSearch: 1,
								refresh: ts,
							},
						})
					);
				}
				const results = yield call(() => Promise.all(promises));
				const bgRows = [];
				results.forEach((r) => {
					const rows = r?.data?.data || r?.data || [];
					if (Array.isArray(rows)) bgRows.push(...rows);
				});

				console.log(`${logPrefix} All pages loaded:`, allRows.length + bgRows.length, 'rows');

				yield put(slice.appendOrderData({ rows: bgRows }));
				yield put(updateGridData({ tableId, data: [...allRows, ...bgRows].map((r) => ({ ...r })) }));
				yield put(slice.setLoading({ backgroundData: false }));
			}
		} catch (e) {
			console.error(`${logPrefix} Order data fetch failed:`, e);
			yield put(slice.setLoading({ data: false, backgroundData: false }));
		}
	};
}

/* ------------------------------------------------------------------ */
/*  createFetchAvailabilitySaga                                       */
/* ------------------------------------------------------------------ */

/**
 * Factory: creates a generator for fetching availability data.
 * Used by orderBySku (skuId) and orderByKit (kitId).
 *
 * @param idField    — the payload property that holds the entity ID (e.g. 'skuId' | 'kitId')
 * @param logPrefix  — console log prefix
 * @param slice      — the variant's Redux slice (has setAvailability, setLoading)
 */
export function createFetchAvailabilitySaga({ idField, logPrefix, slice }) {
	return function* handleFetchAvailability({ payload }) {
		const entityId = payload[idField];
		if (!entityId) {
			yield put(slice.setAvailability({}));
			return;
		}
		try {
			yield put(slice.setLoading({ availability: true }));

			const filter = {
				field: 'SKU',
				value: entityId,
				operator: 'eq',
				symbol: '=',
				type: 'text',
				queryName: 'SKU',
			};
			const res = yield call(postAPIDataV3, {
				service: REQUESTS.POST.GET_AVAILABILITY_INFO.service,
				url: REQUESTS.POST.GET_AVAILABILITY_INFO.url,
				body: {
					search: JSON.stringify({ filter: [filter] }),
					isView: false,
				},
			});

			yield put(slice.setAvailability(res?.data || {}));
		} catch (e) {
			console.error(`${logPrefix} Availability fetch failed:`, e);
			yield put(slice.setAvailability({}));
		} finally {
			yield put(slice.setLoading({ availability: false }));
		}
	};
}
